import React, { JSX, useCallback, useEffect, useState } from 'react'
import {
  CheckBadgeIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useMerchSurfaceVisible } from 'hooks/useMerchSurfaceVisible'
import {
  ProviderBrandIcon,
  TWITCH_BRAND_ICON_SRC,
  YOUTUBE_BRAND_ICON_SRC
} from 'components/ProviderBrandIcon'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { GoogleSignInButton } from 'components/GoogleSignInButton'
import { YouTubeConsentDialog } from 'components/YouTubeConsentDialog'
import { ShopifyConnectCard } from 'components/ShopifyConnectCard'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { YOUTUBE_TERMS_URL } from 'lib/externalLinks'
import {
  Connection,
  TwitchConnectUrl,
  DisconnectConnectionResult,
  DeleteYouTubeDataResult,
  YouTubeConnectUrl,
  YouTubeConsentResult,
  YouTubeWaitlistResult
} from 'types/api'
import listMyConnectionsQuery from 'graphql/listMyConnections.graphql'
import requestTwitchConnectUrlMutation from 'graphql/requestTwitchConnectUrl.graphql'
import requestYouTubeConnectUrlMutation from 'graphql/requestYouTubeConnectUrl.graphql'
import recordYouTubeConsentMutation from 'graphql/recordYouTubeConsent.graphql'
import disconnectConnectionMutation from 'graphql/disconnectConnection.graphql'
import deleteMyYouTubeDataMutation from 'graphql/deleteMyYouTubeData.graphql'
import joinYouTubeWaitlistMutation from 'graphql/joinYouTubeWaitlist.graphql'

const STATE_ERROR_COPY = 'That connect link expired or was already used, please try again.'
const OUR_SIDE_ERROR_COPY = 'Connecting to Twitch failed on our side, please try again.'
const CONNECT_ERROR_COPY: Record<string, string> = {
  state_missing: STATE_ERROR_COPY,
  state_invalid: STATE_ERROR_COPY,
  state_expired: STATE_ERROR_COPY,
  twitch_denied: 'Twitch authorization was cancelled.',
  code_missing: OUR_SIDE_ERROR_COPY,
  exchange_failed: OUR_SIDE_ERROR_COPY,
  identity_failed: OUR_SIDE_ERROR_COPY,
  storage_failed: OUR_SIDE_ERROR_COPY,
  google_denied: 'Google authorization was cancelled.',
  no_channel: 'That Google account has no YouTube channel. Sign in with the Google account that owns your channel and try again.',
  youtube_not_invited: 'YouTube preview access changed before the connection completed, contact us if this is unexpected.',
  youtube_consent_required: 'Please review and agree to the YouTube terms, then try connecting again.'
}

interface ReturnBanner {
  kind: 'success' | 'error'
  message: string
  code: string | null
}

const REQUIRED_SCOPES: Array<{ scope: string, feature: string }> = [
  { scope: 'channel:bot', feature: 'Bot chat access: our bot can read your chat without being modded' },
  { scope: 'channel:read:subscriptions', feature: 'Sub goals, coming with FlightDeck Pro and Creator' },
  { scope: 'channel:read:goals', feature: 'Creator goal mirroring, coming with FlightDeck Pro and Creator' },
  { scope: 'channel:manage:moderators', feature: 'Auto-mod: we add our bot as a moderator for you' },
  { scope: 'user:read:moderated_channels', feature: 'Moderator verification, coming with FlightDeck Pro and Creator' }
]

const isMissingBundleScope = (granted: string[]): boolean =>
  REQUIRED_SCOPES.some(({ scope }) => !granted.includes(scope))

const PreviewChip = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
    {children}
  </span>
)

const StatusBadge = ({ status }: { status: string }): JSX.Element => {
  if (status === 'needs_reconnect') {
    return (
      <span
        data-testid='twitch-needs-reconnect-badge'
        className='inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400 ring-1 ring-inset ring-amber-500/30'
        title='Twitch no longer accepts our stored authorization. Reconnect to refresh it'
      >
        <ShieldExclamationIcon className='size-3' aria-hidden='true' />
        Reconnect needed
      </span>
    )
  }
  return (
    <span
      className='inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 ring-1 ring-inset ring-emerald-500/30'
      title='Your Twitch identity is connected'
    >
      <CheckBadgeIcon className='size-3' aria-hidden='true' />
      Connected
    </span>
  )
}

const TwitchAvatar = ({ connection }: { connection: Connection }): JSX.Element => {
  if (connection.avatarUrl !== null && connection.avatarUrl !== '') {
    return (
      <img
        src={connection.avatarUrl}
        alt=''
        className='h-14 w-14 shrink-0 rounded-full ring-1 ring-white/10'
      />
    )
  }
  return (
    <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand/15 text-base font-semibold text-brand'>
      {(connection.displayName ?? connection.login ?? '??').slice(0, 2).toUpperCase()}
    </div>
  )
}

const ScopeRow = ({
  scope,
  feature,
  granted
}: {
  scope: string
  feature: string
  granted: boolean
}): JSX.Element => {
  if (granted) {
    return (
      <li className='flex items-start gap-2 text-xs'>
        <CheckIcon className='mt-0.5 size-3.5 shrink-0 text-emerald-400' aria-hidden='true' />
        <span className='min-w-0 flex-1 text-dm-text-secondary'>
          {feature}{' '}
          <code className='rounded bg-dm-bg-accent/40 px-1 py-0.5 font-mono text-[10px] text-dm-text-accent'>{scope}</code>
        </span>
      </li>
    )
  }
  return (
    <li className='flex items-start gap-2 text-xs'>
      <ExclamationTriangleIcon className='mt-0.5 size-3.5 shrink-0 text-amber-400' aria-hidden='true' />
      <span className='min-w-0 flex-1 text-amber-300'>
        {feature}{' '}
        <code className='rounded bg-dm-bg-accent/40 px-1 py-0.5 font-mono text-[10px] text-amber-300/80'>{scope}</code>{' '}
        <span className='font-medium'>Not granted. Reconnect to enable</span>
      </span>
    </li>
  )
}

const ScopeLedger = ({ scopes }: { scopes: string[] }): JSX.Element => {
  const extras = scopes.filter((s) => !REQUIRED_SCOPES.some(({ scope }) => scope === s))
  return (
    <div
      data-testid='twitch-scope-ledger'
      className='mt-4 rounded-lg border border-white/5 bg-dm-bg-accent/20 p-3'
    >
      <h5 className='text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
        Permissions
      </h5>
      <ul className='mt-2 space-y-1.5'>
        {REQUIRED_SCOPES.map(({ scope, feature }) => (
          <ScopeRow key={scope} scope={scope} feature={feature} granted={scopes.includes(scope)} />
        ))}
        {extras.map((scope) => (
          <li key={scope} className='flex items-start gap-2 text-xs text-dm-text-secondary'>
            <CheckIcon className='mt-0.5 size-3.5 shrink-0 text-dm-text-accent' aria-hidden='true' />
            <code className='rounded bg-dm-bg-accent/40 px-1 py-0.5 font-mono text-[10px]'>{scope}</code>
          </li>
        ))}
      </ul>
    </div>
  )
}

const BotModStatusLine = ({ status }: { status: string | null }): JSX.Element | null => {
  if (status === 'modded') {
    return (
      <p data-testid='bot-mod-status' className='mt-3 text-xs text-emerald-300'>
        ✓ Our bot is a moderator in your channel
      </p>
    )
  }
  if (status === 'blocked') {
    return (
      <p data-testid='bot-mod-status' className='mt-3 text-xs text-amber-300'>
        We couldn&apos;t add our bot as a moderator (it may be banned or a
        VIP in your channel) mod it manually with{' '}
        <code className='rounded bg-dm-bg-accent/40 px-1 py-0.5 font-mono text-[11px]'>/mod &lt;bot&gt;</code>
      </p>
    )
  }
  return null
}

const DisconnectedBody = ({
  connecting,
  onConnect
}: {
  connecting: boolean
  onConnect: () => void
}): JSX.Element => (
  <div>
    <p className='text-sm text-dm-text-secondary'>
      Connect your Twitch account for use in interactive overlays.
    </p>
    <button
      onClick={onConnect}
      disabled={connecting}
      data-testid='connect-twitch-button'
      className='mt-4 inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
    >
      {connecting ? 'Opening Twitch…' : 'Connect Twitch'}
    </button>
  </div>
)

const ConnectedBody = ({
  connection,
  connecting,
  disconnecting,
  onConnect,
  onDisconnect
}: {
  connection: Connection
  connecting: boolean
  disconnecting: boolean
  onConnect: () => void
  onDisconnect: () => void
}): JSX.Element => {
  const isConnected = connection.status === 'connected'
  const showUpgrade = isConnected && isMissingBundleScope(connection.scopes)
  return (
    <div>
      <div className='flex items-start gap-4'>
        <TwitchAvatar connection={connection} />
        <div className='min-w-0 flex-1'>
          <h4 className='truncate text-lg font-semibold text-dm-text-primary'>
            {connection.displayName ?? connection.login ?? 'Twitch user'}
          </h4>
          {connection.login !== null && (
            <p className='mt-0.5 truncate text-sm text-dm-text-secondary'>@{connection.login}</p>
          )}
          {connection.connectedAt !== null && (
            <p className='mt-1 text-xs text-dm-text-accent'>
              Connected {formatLocalDateTime(connection.connectedAt)}
            </p>
          )}
        </div>
      </div>
      {isConnected && <BotModStatusLine status={connection.botModStatus} />}
      {isConnected && <ScopeLedger scopes={connection.scopes} />}
      <div className='mt-4 flex flex-wrap items-center gap-2'>
        {connection.status === 'needs_reconnect' && (
          <button
            onClick={onConnect}
            disabled={connecting || disconnecting}
            data-testid='reconnect-twitch-button'
            className='inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {connecting ? 'Opening Twitch…' : 'Reconnect'}
          </button>
        )}
        {showUpgrade && (
          <button
            onClick={onConnect}
            disabled={connecting || disconnecting}
            data-testid='update-permissions-button'
            className='inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {connecting ? 'Opening Twitch…' : 'Update permissions'}
          </button>
        )}
        <button
          onClick={onDisconnect}
          disabled={disconnecting || connecting}
          data-testid='disconnect-twitch-button'
          className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {disconnecting ? 'Disconnecting…' : 'Disconnect'}
        </button>
      </div>
    </div>
  )
}

const TwitchCardBody = ({
  twitch,
  loading,
  loadError,
  connecting,
  disconnecting,
  onConnect,
  onDisconnect,
  onRetry
}: {
  twitch: Connection | null
  loading: boolean
  loadError: string | null
  connecting: boolean
  disconnecting: boolean
  onConnect: () => void
  onDisconnect: () => void
  onRetry: () => void
}): JSX.Element => {
  if (loadError !== null) {
    return (
      <div
        data-testid='connections-load-error'
        className='flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300'
      >
        <ExclamationTriangleIcon className='size-5 shrink-0' aria-hidden='true' />
        <div className='flex-1'>
          <p className='font-medium'>Couldn&apos;t load connections</p>
          <p className='mt-1 text-xs text-red-300/80'>{loadError}</p>
        </div>
        <button
          onClick={onRetry}
          className='shrink-0 rounded-md border border-red-500/30 px-3 py-1.5 text-xs font-medium hover:bg-red-500/20'
        >
          Retry
        </button>
      </div>
    )
  }
  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <LoadingSpinner />
      </div>
    )
  }
  if (twitch === null) {
    return <DisconnectedBody connecting={connecting} onConnect={onConnect} />
  }
  return (
    <ConnectedBody
      connection={twitch}
      connecting={connecting}
      disconnecting={disconnecting}
      onConnect={onConnect}
      onDisconnect={onDisconnect}
    />
  )
}

const YouTubeStatusBadge = ({ status }: { status: string }): JSX.Element => {
  if (status === 'needs_reconnect') {
    return (
      <span
        data-testid='youtube-needs-reconnect-badge'
        className='inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400 ring-1 ring-inset ring-amber-500/30'
        title='Google no longer accepts our stored authorization. Reconnect to refresh it'
      >
        <ShieldExclamationIcon className='size-3' aria-hidden='true' />
        Reconnect needed
      </span>
    )
  }
  return (
    <span
      className='inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 ring-1 ring-inset ring-emerald-500/30'
      title='Your YouTube channel is connected'
    >
      <CheckBadgeIcon className='size-3' aria-hidden='true' />
      Connected
    </span>
  )
}

const YouTubeTermsFooter = (): JSX.Element => (
  <p className='mt-4 border-t border-white/5 pt-3 text-[11px] text-dm-text-accent'>
    YouTube API Services are subject to the{' '}
    <a
      href={YOUTUBE_TERMS_URL}
      target='_blank'
      rel='noreferrer'
      className='font-medium text-brand underline hover:text-brand/80'
    >
      YouTube Terms of Service
    </a>.
  </p>
)

const PARTIAL_DELETE_FALLBACK =
  'Some stored data could not be deleted just now, please try again. Nothing new was kept, and anything not removed will expire automatically within 7 days.'

const MandatoryDeleteCopy = (): JSX.Element => (
  <p className='text-sm font-semibold text-dm-text-primary'>
    Deleting your data from StagePilot does not delete or change anything
    on YouTube or in your Google account.
  </p>
)

const YouTubeRevocationExplainer = (): JSX.Element => (
  <p
    data-testid='youtube-revocation-explainer'
    className='mt-3 rounded-md border border-white/5 bg-dm-bg-accent/20 p-3 text-[11px] leading-relaxed text-dm-text-secondary'
  >
    Disconnecting immediately revokes StagePilot&apos;s access at Google
    and deletes the stored connection and your stored YouTube
    data. You can also review or remove StagePilot&apos;s access at any
    time in your Google Account&apos;s third-party access settings (
    <a
      href='https://myaccount.google.com/permissions'
      target='_blank'
      rel='noopener noreferrer'
      className='font-medium text-brand underline hover:text-brand/80'
    >
      myaccount.google.com/permissions
    </a>
    ).
  </p>
)

const YouTubeDeleteDataDialog = ({
  busy,
  error,
  onConfirm,
  onClose,
  onDismissError
}: {
  busy: boolean
  error: string | null
  onConfirm: () => void
  onClose: () => void
  onDismissError: () => void
}): JSX.Element => (
  <div
    className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
    data-testid='youtube-delete-data-dialog'
  >
    <div className='max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-white/10 bg-dm-bg-secondary p-6 shadow-2xl'>
      <h2 className='text-lg font-semibold text-dm-text-primary'>
        Delete my stored YouTube data
      </h2>
      <div className='mt-3'>
        <MandatoryDeleteCopy />
      </div>
      <p className='mt-3 text-sm text-dm-text-secondary'>
        This permanently deletes everything StagePilot has stored from your
        YouTube activity:
      </p>
      <ul className='mt-2 list-disc space-y-2 pl-5 text-sm text-dm-text-secondary'>
        <li>
          Your chat-session record, including broadcast and live-chat
          identifiers.
        </li>
        <li>
          Per-vote audit records and voting locks created from YouTube chat
          messages. (StagePilot never stores YouTube chat text or author
          names.)
        </li>
        <li>
          YouTube vote tallies on your polls and tug-of-wars. These are
          reset to zero. Twitch tallies are not affected.
        </li>
      </ul>
      <p className='mt-3 text-sm text-dm-text-secondary'>
        Your YouTube connection, your consent record, and your preview
        invite are kept. To also remove StagePilot&apos;s access to your
        Google account, use Disconnect.
      </p>
      {error !== null && (
        <div className='mt-3'>
          <ErrorInline onDismiss={onDismissError}>{error}</ErrorInline>
        </div>
      )}
      <div className='mt-5 flex items-center justify-end gap-2'>
        <button
          type='button'
          onClick={onClose}
          disabled={busy}
          data-testid='youtube-delete-data-cancel'
          className='rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={onConfirm}
          disabled={busy}
          data-testid='youtube-delete-data-confirm'
          className='inline-flex items-center gap-2 rounded-md bg-red-500/20 px-4 py-2 text-sm font-semibold text-red-200 ring-1 ring-inset ring-red-500/40 hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {busy && <LoadingSpinner />}
          {busy ? 'Deleting…' : 'Delete stored data'}
        </button>
      </div>
    </div>
  </div>
)

const YouTubeDeleteOutcome = ({
  outcome,
  onDismiss
}: {
  outcome: DeleteYouTubeDataResult | null
  onDismiss: () => void
}): JSX.Element | null => {
  if (outcome === null) return null
  if (outcome.completed) {
    return (
      <div
        data-testid='youtube-delete-data-success'
        className='mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3'
      >
        <div className='flex items-start gap-2'>
          <CheckIcon className='mt-0.5 size-4 shrink-0 text-emerald-300' aria-hidden='true' />
          <div className='flex-1 text-xs text-emerald-300'>
            <p className='font-semibold'>Your stored YouTube data has been deleted.</p>
            <p className='mt-1 text-emerald-300/90'>
              We removed your chat-session record and every YouTube vote
              record across {outcome.purgedEvents} stream{outcome.purgedEvents === 1 ? '' : 's'},
              and reset YouTube vote tallies to zero.
            </p>
          </div>
          <button
            type='button'
            onClick={onDismiss}
            aria-label='Dismiss'
            title='Dismiss'
            className='-m-1 shrink-0 rounded p-1 text-emerald-300/70 transition-colors hover:bg-emerald-500/10 hover:text-emerald-200'
          >
            <XMarkIcon className='size-3.5' aria-hidden='true' />
          </button>
        </div>
        <div className='mt-2 border-t border-emerald-500/20 pt-2'>
          <MandatoryDeleteCopy />
        </div>
      </div>
    )
  }
  return (
    <div
      data-testid='youtube-delete-data-partial'
      className='mt-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300'
    >
      <ExclamationTriangleIcon className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
      <span className='flex-1'>{outcome.message ?? PARTIAL_DELETE_FALLBACK}</span>
      <button
        type='button'
        onClick={onDismiss}
        aria-label='Dismiss'
        title='Dismiss'
        className='-m-1 shrink-0 rounded p-1 text-amber-300/70 transition-colors hover:bg-amber-500/10 hover:text-amber-200'
      >
        <XMarkIcon className='size-3.5' aria-hidden='true' />
      </button>
    </div>
  )
}

const YouTubeWaitlistBlock = ({
  waitlistAt,
  joining,
  onJoin
}: {
  waitlistAt: string | null
  joining: boolean
  onJoin: () => void
}): JSX.Element => {
  if (waitlistAt !== null) {
    return (
      <p
        data-testid='youtube-waitlist-joined'
        className='mt-2 flex items-start gap-2 text-xs text-emerald-300'
      >
        <CheckIcon className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
        <span>
          You&apos;re on the waitlist. We&apos;ll invite streamers in small
          groups as the preview expands.
        </span>
      </p>
    )
  }
  return (
    <div>
      <button
        type='button'
        onClick={onJoin}
        disabled={joining}
        data-testid='youtube-join-waitlist-button'
        className='mt-3 inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
      >
        {joining && <LoadingSpinner />}
        {joining ? 'Joining…' : 'Join the waitlist'}
      </button>
    </div>
  )
}

const YouTubeConnectedBody = ({
  connection,
  connecting,
  disconnecting,
  deleting,
  onConnect,
  onDisconnect,
  onDeleteData
}: {
  connection: Connection
  connecting: boolean
  disconnecting: boolean
  deleting: boolean
  onConnect: () => void
  onDisconnect: () => void
  onDeleteData: () => void
}): JSX.Element => {
  return (
    <div>
      <div className='flex items-start gap-4'>
        <TwitchAvatar connection={connection} />
        <div className='min-w-0 flex-1'>
          <h4 className='truncate text-lg font-semibold text-dm-text-primary'>
            {connection.displayName ?? 'YouTube channel'}
          </h4>
          {connection.connectedAt !== null && (
            <p className='mt-1 text-xs text-dm-text-accent'>
              Connected {formatLocalDateTime(connection.connectedAt)}
            </p>
          )}
        </div>
      </div>
      <div className='mt-4 flex flex-wrap items-center gap-2'>
        {connection.status === 'needs_reconnect' && (
          <GoogleSignInButton
            onClick={onConnect}
            busy={connecting}
            disabled={disconnecting}
            data-testid='reconnect-youtube-button'
          />
        )}
        <button
          onClick={onDisconnect}
          disabled={disconnecting || connecting || deleting}
          data-testid='disconnect-youtube-button'
          className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {disconnecting ? 'Disconnecting…' : 'Disconnect'}
        </button>
        <button
          onClick={onDeleteData}
          disabled={disconnecting || connecting || deleting}
          data-testid='youtube-delete-data-button'
          className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60'
        >
          Delete my stored YouTube data
        </button>
      </div>
      <YouTubeRevocationExplainer />
    </div>
  )
}

const YouTubeCardBody = ({
  invited,
  consented,
  entReady,
  youtube,
  connectionsLoading,
  connecting,
  disconnecting,
  deleting,
  waitlistAt,
  joiningWaitlist,
  onReviewConnect,
  onConnect,
  onDisconnect,
  onDeleteData,
  onJoinWaitlist
}: {
  invited: boolean
  consented: boolean
  entReady: boolean
  youtube: Connection | null
  connectionsLoading: boolean
  connecting: boolean
  disconnecting: boolean
  deleting: boolean
  waitlistAt: string | null
  joiningWaitlist: boolean
  onReviewConnect: () => void
  onConnect: () => void
  onDisconnect: () => void
  onDeleteData: () => void
  onJoinWaitlist: () => void
}): JSX.Element => {
  if (!entReady) {
    return (
      <div className='flex items-center justify-center py-8'>
        <LoadingSpinner />
      </div>
    )
  }
  if (!invited) {
    return (
      <div>
        <p className='text-sm text-dm-text-secondary'>
          YouTube live chat is in an invite-only preview. We&apos;re inviting streamers in
          small groups to keep the experience reliable for everyone.
        </p>
        <YouTubeWaitlistBlock
          waitlistAt={waitlistAt}
          joining={joiningWaitlist}
          onJoin={onJoinWaitlist}
        />
      </div>
    )
  }
  if (!consented) {
    return (
      <div>
        <p className='text-sm text-dm-text-secondary'>
          Review the YouTube terms and connect your channel to use your live chat in interactive overlays.
        </p>
        <button
          onClick={onReviewConnect}
          data-testid='youtube-review-connect-button'
          className='mt-4 inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80'
        >
          Review &amp; connect
        </button>
      </div>
    )
  }
  if (connectionsLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <LoadingSpinner />
      </div>
    )
  }
  if (youtube === null) {
    return (
      <div>
        <p className='text-sm text-dm-text-secondary'>
          You&apos;ve agreed to the YouTube terms. Connect the Google account
          that owns your channel, we request read-only access to your live
          chat, nothing more.
        </p>
        <div className='mt-4'>
          <GoogleSignInButton
            onClick={onConnect}
            busy={connecting}
            disabled={disconnecting}
            data-testid='connect-youtube-button'
          />
        </div>
      </div>
    )
  }
  return (
    <YouTubeConnectedBody
      connection={youtube}
      connecting={connecting}
      disconnecting={disconnecting}
      deleting={deleting}
      onConnect={onConnect}
      onDisconnect={onDisconnect}
      onDeleteData={onDeleteData}
    />
  )
}

const youTubeHeaderRight = (
  entReady: boolean,
  invited: boolean,
  youtube: Connection | null
): JSX.Element | null => {
  if (!entReady) return null
  if (!invited) return <PreviewChip>Invite-only preview</PreviewChip>
  if (youtube !== null) return <YouTubeStatusBadge status={youtube.status} />
  return <></>
}

export const ConnectionsPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const {
    account,
    status: entStatus,
    refetch: refetchAccount
  } = useEntitlements()
  const merchSurfaceVisible = useMerchSurfaceVisible()
  const [connections, setConnections] = useState<Connection[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [banner, setBanner] = useState<ReturnBanner | null>(null)
  const [ytConnecting, setYtConnecting] = useState(false)
  const [ytDisconnecting, setYtDisconnecting] = useState(false)
  const [ytActionError, setYtActionError] = useState<string | null>(null)
  const [consentOpen, setConsentOpen] = useState(false)
  const [consentBusy, setConsentBusy] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteOutcome, setDeleteOutcome] = useState<DeleteYouTubeDataResult | null>(null)
  const [waitlistJoining, setWaitlistJoining] = useState(false)
  const [waitlistJoinedAt, setWaitlistJoinedAt] = useState<string | null>(null)

  const fetchConnections = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setLoadError(null)
    try {
      const resp = await graphqlRequest<{ connections: Connection[] }>(
        listMyConnectionsQuery, {}, auth0Token
      )
      setConnections(resp.connections ?? [])
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Unknown error')
    }
  }, [auth0Token])

  useEffect(() => {
    void fetchConnections()
  }, [fetchConnections])

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search)
    const connected = params.get('connected')
    const errorCode = params.get('error')
    const isProvider = connected === 'twitch' || connected === 'youtube'
    if (!isProvider && errorCode === null) return
    if (isProvider) {
      const message = connected === 'youtube' ? 'YouTube connected.' : 'Twitch connected.'
      setBanner({ kind: 'success', message, code: null })
      void fetchConnections()
    } else if (errorCode !== null) {
      setBanner({
        kind: 'error',
        message: CONNECT_ERROR_COPY[errorCode] ?? OUR_SIDE_ERROR_COPY,
        code: errorCode
      })
    }
    params.delete('connected')
    params.delete('error')
    const rest = params.toString()
    globalThis.history.replaceState(
      null, '', globalThis.location.pathname + (rest.length > 0 ? `?${rest}` : '')
    )
  }, [fetchConnections])

  const handleConnect = async (): Promise<void> => {
    if (auth0Token === undefined || connecting || disconnecting) return
    setConnecting(true)
    setActionError(null)
    try {
      const { connectUrl } = await graphqlRequest<{ connectUrl: TwitchConnectUrl }>(
        requestTwitchConnectUrlMutation,
        { returnOrigin: globalThis.location.origin },
        auth0Token
      )
      globalThis.location.assign(connectUrl.url)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start Twitch connect')
      setConnecting(false)
    }
  }

  const handleDisconnect = async (): Promise<void> => {
    if (auth0Token === undefined || disconnecting || connecting) return
    const ok = globalThis.confirm(
      'Disconnect Twitch? Stored tokens are revoked and deleted. Your Streams and their vote channels are not affected.'
    )
    if (!ok) return
    setDisconnecting(true)
    setActionError(null)
    try {
      await graphqlRequest<{ result: DisconnectConnectionResult }>(
        disconnectConnectionMutation, { provider: 'twitch' }, auth0Token
      )
      setConnections((prev) => prev === null ? prev : prev.filter((c) => c.provider !== 'twitch'))
      void fetchConnections()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to disconnect Twitch')
    } finally {
      setDisconnecting(false)
    }
  }

  const startGoogleConnect = async (token: string): Promise<void> => {
    const { connectUrl } = await graphqlRequest<{ connectUrl: YouTubeConnectUrl }>(
      requestYouTubeConnectUrlMutation,
      { returnOrigin: globalThis.location.origin },
      token
    )
    globalThis.location.assign(connectUrl.url)
  }

  const handleYouTubeConnect = async (): Promise<void> => {
    if (auth0Token === undefined || ytConnecting || ytDisconnecting) return
    setYtConnecting(true)
    setYtActionError(null)
    try {
      await startGoogleConnect(auth0Token)
    } catch (err) {
      setYtActionError(err instanceof Error ? err.message : 'Failed to start YouTube connect')
      setYtConnecting(false)
    }
  }

  const handleConsentAgree = async (): Promise<void> => {
    if (auth0Token === undefined || consentBusy) return
    setConsentBusy(true)
    setYtActionError(null)
    try {
      await graphqlRequest<{ result: YouTubeConsentResult }>(
        recordYouTubeConsentMutation, {}, auth0Token
      )
    } catch (err) {
      setYtActionError(err instanceof Error ? err.message : 'Failed to record YouTube consent')
      setConsentBusy(false)
      setConsentOpen(false)
      return
    }
    void refetchAccount()
    try {
      await startGoogleConnect(auth0Token)
    } catch (err) {
      setYtActionError(err instanceof Error ? err.message : 'Failed to start YouTube connect')
      setConsentBusy(false)
      setConsentOpen(false)
    }
  }

  const handleYouTubeDisconnect = async (): Promise<void> => {
    if (auth0Token === undefined || ytDisconnecting || ytConnecting) return
    const ok = globalThis.confirm(
      'Disconnect YouTube? We immediately revoke our access with Google and delete the stored connection, its tokens, and your stored YouTube data. Nothing on your YouTube channel or in your Google account is affected.'
    )
    if (!ok) return
    setYtDisconnecting(true)
    setYtActionError(null)
    try {
      await graphqlRequest<{ result: DisconnectConnectionResult }>(
        disconnectConnectionMutation, { provider: 'youtube' }, auth0Token
      )
      setConnections((prev) => prev === null ? prev : prev.filter((c) => c.provider !== 'youtube'))
      void fetchConnections()
    } catch (err) {
      setYtActionError(err instanceof Error ? err.message : 'Failed to disconnect YouTube')
    } finally {
      setYtDisconnecting(false)
    }
  }

  const handleYouTubeDeleteData = async (): Promise<void> => {
    if (auth0Token === undefined || deleteBusy) return
    setDeleteBusy(true)
    setDeleteError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeleteYouTubeDataResult }>(
        deleteMyYouTubeDataMutation, {}, auth0Token
      )
      setDeleteOutcome(result)
      setDeleteDialogOpen(false)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete stored YouTube data')
    } finally {
      setDeleteBusy(false)
    }
  }

  const handleOpenDeleteDialog = (): void => {
    setDeleteOutcome(null)
    setDeleteError(null)
    setDeleteDialogOpen(true)
  }

  const handleJoinWaitlist = async (): Promise<void> => {
    if (auth0Token === undefined || waitlistJoining) return
    setWaitlistJoining(true)
    setYtActionError(null)
    try {
      const { result } = await graphqlRequest<{ result: YouTubeWaitlistResult }>(
        joinYouTubeWaitlistMutation, {}, auth0Token
      )
      setWaitlistJoinedAt(result.youtubeWaitlistAt)
      void refetchAccount()
    } catch (err) {
      setYtActionError(err instanceof Error ? err.message : 'Failed to join the waitlist')
    } finally {
      setWaitlistJoining(false)
    }
  }

  const twitch = connections?.find((c) => c.provider === 'twitch') ?? null
  const youtube = connections?.find((c) => c.provider === 'youtube') ?? null
  const loading = connections === null && loadError === null
  const invited = account?.youtubeChatInvited === true
  const consented = typeof account?.youtubeConsentAt === 'string' && account.youtubeConsentAt.length > 0
  const entReady = entStatus === 'ready' && account !== null
  const waitlistAt = waitlistJoinedAt ?? account?.youtubeWaitlistAt ?? null

  return (
    <div className='flex flex-1' data-testid='connections-page'>
      <main className='flex flex-1 flex-col px-4 py-4'>
        <header className='mb-1 flex items-center gap-3'>
          <h1 className='text-2xl font-semibold text-dm-text-primary'>Connections</h1>
        </header>
        <p className='mb-4 text-sm text-dm-text-secondary'>
          Link your accounts, your overlays can combine multiple third-party platforms
        </p>

        {banner !== null && banner.kind === 'success' && (
          <div
            data-testid='connections-return-success'
            className='mb-4 flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300'
          >
            <CheckIcon className='size-4 shrink-0' aria-hidden='true' />
            <span className='flex-1'>{banner.message}</span>
            <button
              type='button'
              onClick={() => setBanner(null)}
              aria-label='Dismiss'
              title='Dismiss'
              className='-m-1 shrink-0 rounded p-1 text-emerald-300/70 transition-colors hover:bg-emerald-500/10 hover:text-emerald-200'
            >
              <XMarkIcon className='size-3.5' aria-hidden='true' />
            </button>
          </div>
        )}
        {banner !== null && banner.kind === 'error' && (
          <div className='mb-4' data-testid='connections-return-error'>
            <ErrorInline onDismiss={() => setBanner(null)}>
              {banner.message}
              {banner.code !== null && (
                <>{' '}<span className='font-mono text-red-300/60'>({banner.code})</span></>
              )}
            </ErrorInline>
          </div>
        )}

        <div className='grid gap-4 md:grid-cols-2'>
          <section
            data-testid='twitch-connection-card'
            className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'
          >
            <div className='mb-3 flex items-center justify-between gap-3'>
              <div className='flex min-w-0 items-center gap-3'>
                <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} />
                <h3 className='truncate text-base font-semibold text-dm-text-primary'>Twitch</h3>
              </div>
              {twitch !== null && <StatusBadge status={twitch.status} />}
            </div>
            <TwitchCardBody
              twitch={twitch}
              loading={loading}
              loadError={loadError}
              connecting={connecting}
              disconnecting={disconnecting}
              onConnect={() => { void handleConnect() }}
              onDisconnect={() => { void handleDisconnect() }}
              onRetry={() => { void fetchConnections() }}
            />
            {actionError !== null && (
              <div className='mt-3'>
                <ErrorInline onDismiss={() => setActionError(null)}>{actionError}</ErrorInline>
              </div>
            )}
          </section>

          <section
            data-testid='youtube-connection-card'
            className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'
          >
            <div className='mb-3 flex items-center justify-between gap-3'>
              <div className='flex min-w-0 items-center gap-3'>
                <ProviderBrandIcon src={YOUTUBE_BRAND_ICON_SRC} />
                <h3 className='truncate text-base font-semibold text-dm-text-primary'>YouTube</h3>
              </div>
              {youTubeHeaderRight(entReady, invited, youtube)}
            </div>
            <YouTubeCardBody
              invited={invited}
              consented={consented}
              entReady={entReady}
              youtube={youtube}
              connectionsLoading={loading}
              connecting={ytConnecting}
              disconnecting={ytDisconnecting}
              deleting={deleteBusy}
              waitlistAt={waitlistAt}
              joiningWaitlist={waitlistJoining}
              onReviewConnect={() => setConsentOpen(true)}
              onConnect={() => { void handleYouTubeConnect() }}
              onDisconnect={() => { void handleYouTubeDisconnect() }}
              onDeleteData={handleOpenDeleteDialog}
              onJoinWaitlist={() => { void handleJoinWaitlist() }}
            />
            <YouTubeDeleteOutcome
              outcome={deleteOutcome}
              onDismiss={() => setDeleteOutcome(null)}
            />
            {ytActionError !== null && (
              <div className='mt-3'>
                <ErrorInline onDismiss={() => setYtActionError(null)}>{ytActionError}</ErrorInline>
              </div>
            )}
            <YouTubeTermsFooter />
          </section>
          {merchSurfaceVisible && <ShopifyConnectCard auth0Token={auth0Token} />}
        </div>

        {consentOpen && (
          <YouTubeConsentDialog
            onAgree={() => { void handleConsentAgree() }}
            onClose={() => setConsentOpen(false)}
            busy={consentBusy}
          />
        )}

        {deleteDialogOpen && (
          <YouTubeDeleteDataDialog
            busy={deleteBusy}
            error={deleteError}
            onConfirm={() => { void handleYouTubeDeleteData() }}
            onClose={() => { setDeleteDialogOpen(false); setDeleteError(null) }}
            onDismissError={() => setDeleteError(null)}
          />
        )}
      </main>
    </div>
  )
}
